import React, { useState, useEffect, useCallback } from 'react';
import * as API from '../api';
import { FONT, TEXT, TEXT2, TEXT3, BLUE, BLUE_LIGHT, BORDER, SUCCESS, WARNING, DANGER } from '../shared';
import CreativeEditor from './CreativeEditor';
import { SkeletonGrid } from './ui/Skeleton';

const GLASS_CARD = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.82) 0%, rgba(255,255,255,0.68) 100%)",
  backdropFilter: "blur(34px) saturate(180%)",
  WebkitBackdropFilter: "blur(34px) saturate(180%)",
  border: "1px solid rgba(255,255,255,0.40)",
  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.55), 0 6px 18px -10px rgba(11,107,211,0.18)",
};

const PLATFORM_COLOR = {
  Instagram: "#E1306C",
  Facebook: "#1877F2",
  TikTok: "#010101",
  LinkedIn: "#0A66C2",
  Google: "#4285F4",
  YouTube: "#FF0000",
};

const STATUS_STYLE = {
  posted:     { bg: "#DCFCE7", color: "#16A34A", label: "Posted" },
  published:  { bg: "#DCFCE7", color: "#16A34A", label: "Published" },
  scheduled:  { bg: "#FEF9C3", color: "#B45309", label: "Scheduled" },
  draft:      { bg: "#F1F5F9", color: "#64748B", label: "Draft" },
  active:     { bg: "#DBEAFE", color: "#1D4ED8", label: "Active" },
  paused:     { bg: "#FEF3C7", color: "#D97706", label: "Paused" },
  ended:      { bg: "#F1F5F9", color: "#64748B", label: "Ended" },
};

function PlatformDot({ platform, size = 8 }) {
  const color = PLATFORM_COLOR[platform] || BLUE;
  return (
    <span style={{ display: "inline-block", width: size, height: size, borderRadius: "50%", background: color, flexShrink: 0 }} />
  );
}

function StatusBadge({ status }) {
  const s = STATUS_STYLE[status?.toLowerCase()] || STATUS_STYLE.draft;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", background: s.bg, color: s.color, borderRadius: 6, padding: "2px 7px" }}>
      {s.label}
    </span>
  );
}

function PlatformIcon({ platform, size = 16 }) {
  const color = PLATFORM_COLOR[platform] || TEXT2;
  if (platform === "Instagram") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="1" fill={color} stroke="none"/>
    </svg>
  );
  if (platform === "TikTok") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.15 8.15 0 004.77 1.52V6.72a4.85 4.85 0 01-1-.03z"/></svg>
  );
  if (platform === "LinkedIn") return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color}><path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z"/><circle cx="4" cy="4" r="2"/></svg>
  );
  // Facebook / Google / default
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
    </svg>
  );
}

function Thumbnail({ url, type }) {
  if (!url) return (
    <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg, #E0F2FE, #BAE6FD)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#7DD3FC" strokeWidth="1.5" strokeLinecap="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
      </svg>
    </div>
  );
  if (type === "video") return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <video src={url} style={{ width: "100%", height: "100%", objectFit: "cover" }} muted />
      <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", background: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="#fff"><polygon points="2,1 9,5 2,9"/></svg>
        </div>
      </div>
    </div>
  );
  return <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />;
}

function CreativeCard({ item, onExpand, onUseAgain }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={() => onExpand(item)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...GLASS_CARD,
        borderRadius: 16,
        overflow: "hidden",
        cursor: "pointer",
        transform: hover ? "translateY(-3px)" : "none",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        boxShadow: hover
          ? "inset 0 1px 0 rgba(255,255,255,0.7), 0 16px 36px -10px rgba(11,107,211,0.28)"
          : GLASS_CARD.boxShadow,
      }}
    >
      {/* Thumbnail */}
      <div style={{ height: 140, overflow: "hidden", background: "#F1F5F9" }}>
        <Thumbnail url={item.imageUrl || item.image_url || item.url} type={item.type || "image"} />
      </div>

      {/* Info */}
      <div style={{ padding: "10px 12px 12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <PlatformIcon platform={item.platform} size={14} />
          <span style={{ fontSize: 11, fontWeight: 600, color: PLATFORM_COLOR[item.platform] || TEXT3 }}>{item.platform || "Content"}</span>
          <div style={{ flex: 1 }} />
          <StatusBadge status={item.status || item.postStatus || "draft"} />
        </div>

        <p style={{ fontSize: 12, color: TEXT, lineHeight: 1.45, margin: "0 0 8px", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
          {item.caption || item.description || item.name || "No caption"}
        </p>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: 10, color: TEXT3 }}>
            {item.createdAt || item.created_at ? new Date(item.createdAt || item.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
          </span>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onUseAgain(item); }}
            style={{ fontSize: 11, fontWeight: 600, color: BLUE, background: BLUE_LIGHT, border: "none", borderRadius: 7, padding: "4px 10px", cursor: "pointer", fontFamily: FONT }}
          >
            Use Again
          </button>
        </div>
      </div>
    </div>
  );
}

function ExpandModal({ item, onClose, onUseAgain, onEdit }) {
  if (!item) return null;
  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(10,27,58,0.55)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", zIndex: 1200, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ ...GLASS_CARD, borderRadius: 24, width: "100%", maxWidth: 560, maxHeight: "90vh", overflowY: "auto", padding: 24 }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <PlatformIcon platform={item.platform} size={18} />
          <span style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>{item.platform || "Creative"}</span>
          <StatusBadge status={item.status || item.postStatus || "draft"} />
          <div style={{ flex: 1 }} />
          <button onClick={onClose} type="button" style={{ background: "none", border: "none", cursor: "pointer", color: TEXT3, padding: 4 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Asset */}
        {(item.imageUrl || item.image_url || item.url) && (
          <div style={{ borderRadius: 14, overflow: "hidden", marginBottom: 16, maxHeight: 320 }}>
            <Thumbnail url={item.imageUrl || item.image_url || item.url} type={item.type || "image"} />
          </div>
        )}

        {/* Caption */}
        {item.caption && (
          <div style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>Caption</p>
            <p style={{ fontSize: 14, color: TEXT, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>{item.caption}</p>
          </div>
        )}

        {/* Hashtags */}
        {item.hashtags && item.hashtags.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Hashtags</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {(Array.isArray(item.hashtags) ? item.hashtags : item.hashtags.split(/\s+/)).map((h, i) => (
                <span key={i} style={{ fontSize: 12, color: BLUE, background: BLUE_LIGHT, borderRadius: 6, padding: "3px 8px", fontWeight: 500 }}>{h.startsWith("#") ? h : `#${h}`}</span>
              ))}
            </div>
          </div>
        )}

        {/* Performance metrics */}
        {(item.likes != null || item.reach != null || item.impressions != null || item.clicks != null) && (
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Performance</p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))", gap: 8 }}>
              {[
                { label: "Likes", val: item.likes },
                { label: "Reach", val: item.reach },
                { label: "Impressions", val: item.impressions },
                { label: "Clicks", val: item.clicks },
                { label: "Spend", val: item.spend ? `$${item.spend}` : null },
                { label: "ROAS", val: item.roas ? `${item.roas}x` : null },
              ].filter(m => m.val != null).map(m => (
                <div key={m.label} style={{ background: "rgba(255,255,255,0.6)", borderRadius: 10, padding: "10px 12px", textAlign: "center" }}>
                  <p style={{ fontSize: 16, fontWeight: 700, color: TEXT, margin: "0 0 2px" }}>{typeof m.val === "number" ? m.val.toLocaleString() : m.val}</p>
                  <p style={{ fontSize: 10, color: TEXT3, margin: 0, textTransform: "uppercase", letterSpacing: 0.5 }}>{m.label}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Date */}
        <p style={{ fontSize: 11, color: TEXT3, marginBottom: 16 }}>
          Created: {item.createdAt || item.created_at ? new Date(item.createdAt || item.created_at).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Unknown"}
        </p>

        <div style={{ display: "flex", gap: 10 }}>
          {(item.imageUrl || item.image_url || item.url) && (item.type || "image") !== "video" && (
            <button
              type="button"
              onClick={() => { onEdit?.(item); }}
              style={{ flex: 1, padding: "12px 0", borderRadius: 14, border: `1px solid ${BORDER}`, background: "rgba(255,255,255,0.72)", color: TEXT, fontSize: 15, fontWeight: 600, cursor: "pointer", fontFamily: FONT }}
            >
              Edit
            </button>
          )}
          <button
            type="button"
            onClick={() => { onUseAgain(item); onClose(); }}
            style={{ flex: 1, padding: "12px 0", borderRadius: 14, border: "none", background: `linear-gradient(135deg, #38BDF8, ${BLUE} 60%, #0284C7)`, color: "#fff", fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: FONT, boxShadow: "0 8px 24px -6px rgba(14,165,233,0.45)" }}
          >
            Use Again in Vella Relay
          </button>
        </div>
      </div>
    </div>
  );
}

const PLATFORMS = ["All", "Instagram", "Facebook", "TikTok", "LinkedIn", "Google"];
const TYPES = ["All", "Image", "Video", "Carousel"];
const STATUSES = ["All", "Posted", "Scheduled", "Draft", "Active", "Paused"];

export default function CreativeStudioView({ setActive, setVellaPrompt }) {
  const [posts, setPosts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null); // { imageUrl, prompt } when editor is open
  const [filterPlatform, setFilterPlatform] = useState("All");
  const [filterType, setFilterType] = useState("All");
  const [filterStatus, setFilterStatus] = useState("All");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      API.getPostAnalytics({ limit: 50 }).catch(() => null),
      API.getCampaignAnalytics({}).catch(() => null),
    ]).then(([postsData, campsData]) => {
      const rawPosts = postsData?.posts || postsData?.data || (Array.isArray(postsData) ? postsData : []);
      const rawCamps = campsData?.campaigns || campsData?.data || (Array.isArray(campsData) ? campsData : []);
      setPosts(rawPosts.map(p => ({ ...p, _kind: "post" })));
      setCampaigns(rawCamps.map(c => ({ ...c, _kind: "campaign" })));
      setLoading(false);
    }).catch(() => { setError("Could not load creatives."); setLoading(false); });
  }, []);

  const handleUseAgain = useCallback((item) => {
    const cue = item.caption
      ? `Repost this creative on ${item.platform || "social media"}: "${(item.caption || "").slice(0, 100)}"`
      : `Create a similar creative for ${item.platform || "social media"}.`;
    if (setVellaPrompt) setVellaPrompt(cue);
    if (setActive) setActive("talkToVella");
  }, [setActive, setVellaPrompt]);

  const allItems = [...posts, ...campaigns];

  const filtered = allItems.filter(item => {
    if (filterPlatform !== "All" && item.platform !== filterPlatform) return false;
    if (filterType !== "All") {
      const t = (item.type || item.postType || item._kind === "campaign" ? "campaign" : "image").toLowerCase();
      if (filterType === "Video" && !t.includes("video")) return false;
      if (filterType === "Image" && (t.includes("video") || t.includes("carousel"))) return false;
      if (filterType === "Carousel" && !t.includes("carousel")) return false;
    }
    if (filterStatus !== "All") {
      const s = (item.status || item.postStatus || "draft").toLowerCase();
      if (s !== filterStatus.toLowerCase()) return false;
    }
    return true;
  });

  const FilterPill = ({ options, value, onChange }) => (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {options.map(o => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          style={{
            padding: "5px 12px",
            borderRadius: 999,
            border: value === o ? "1px solid rgba(14,165,233,0.6)" : "1px solid rgba(255,255,255,0.5)",
            background: value === o ? BLUE_LIGHT : "rgba(255,255,255,0.55)",
            color: value === o ? BLUE : TEXT2,
            fontSize: 12,
            fontWeight: value === o ? 600 : 400,
            cursor: "pointer",
            fontFamily: FONT,
            transition: "all 0.15s ease",
            whiteSpace: "nowrap",
          }}
        >
          {o}
        </button>
      ))}
    </div>
  );

  return (
    <div style={{ fontFamily: FONT, maxWidth: 1100, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, color: TEXT, margin: "0 0 4px", letterSpacing: "-0.02em" }}>Creative Studio</h2>
        <p style={{ fontSize: 13, color: TEXT3, margin: 0 }}>All your past content and ad creatives in one place.</p>
      </div>

      {/* Filters */}
      <div style={{ ...GLASS_CARD, borderRadius: 16, padding: "14px 18px", marginBottom: 24, display: "flex", flexWrap: "wrap", gap: 14 }}>
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px" }}>Platform</p>
          <FilterPill options={PLATFORMS} value={filterPlatform} onChange={setFilterPlatform} />
        </div>
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px" }}>Type</p>
          <FilterPill options={TYPES} value={filterType} onChange={setFilterType} />
        </div>
        <div>
          <p style={{ fontSize: 10, fontWeight: 700, color: TEXT3, textTransform: "uppercase", letterSpacing: 1, margin: "0 0 6px" }}>Status</p>
          <FilterPill options={STATUSES} value={filterStatus} onChange={setFilterStatus} />
        </div>
      </div>

      {/* Grid */}
      {loading && <SkeletonGrid count={9} columns={3} />}

      {!loading && error && (
        <div style={{ ...GLASS_CARD, borderRadius: 16, padding: 24, textAlign: "center" }}>
          <p style={{ color: DANGER, fontSize: 14, margin: 0 }}>{error}</p>
        </div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div style={{ ...GLASS_CARD, borderRadius: 24, padding: "60px 40px", textAlign: "center" }}>
          <div style={{ width: 56, height: 56, borderRadius: 18, background: BLUE_LIGHT, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
            </svg>
          </div>
          <p style={{ fontSize: 16, fontWeight: 600, color: TEXT, margin: "0 0 8px" }}>
            {allItems.length === 0 ? "No creatives yet" : "No results for these filters"}
          </p>
          <p style={{ fontSize: 13, color: TEXT3, margin: "0 0 20px" }}>
            {allItems.length === 0
              ? "Ask Vella to create your first post in Vella Relay."
              : "Try adjusting the platform, type, or status filters."}
          </p>
          {allItems.length === 0 && setActive && (
            <button
              type="button"
              onClick={() => setActive("talkToVella")}
              style={{ padding: "11px 24px", borderRadius: 14, border: "none", background: `linear-gradient(135deg, #38BDF8, ${BLUE} 60%, #0284C7)`, color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: FONT, boxShadow: "0 8px 24px -6px rgba(14,165,233,0.45)" }}
            >
              Open Vella Relay
            </button>
          )}
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          <p style={{ fontSize: 11, color: TEXT3, marginBottom: 14 }}>{filtered.length} creative{filtered.length !== 1 ? "s" : ""}</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 16 }}>
            {filtered.map((item, i) => (
              <CreativeCard key={item.id || i} item={item} onExpand={setExpanded} onUseAgain={handleUseAgain} />
            ))}
          </div>
        </>
      )}

      <ExpandModal
        item={expanded}
        onClose={() => setExpanded(null)}
        onUseAgain={handleUseAgain}
        onEdit={(item) => {
          const url = item.imageUrl || item.image_url || item.url;
          if (!url) return;
          setEditing({ imageUrl: url, prompt: item.caption || item.description || "" });
          setExpanded(null);
        }}
      />

      <CreativeEditor
        open={!!editing}
        imageUrl={editing?.imageUrl}
        initialPrompt={editing?.prompt}
        onClose={() => setEditing(null)}
        onSave={() => setEditing(null)}
      />
    </div>
  );
}
